// The page frame: brand, main navigation, last upload and Account on top; on a phone, a floating
// tab bar instead.

import { formatRelativeMoment } from "../domain/format.ts";
import { linkHandler } from "../lib/router.ts";
import { Icon } from "./Icon.tsx";

export type Section = "trips" | "mileage" | "export" | "account";

const sections: { id: Exclude<Section, "account">; title: string; icon: string; path: string }[] = [
  { id: "trips", title: "Trips", icon: "trips", path: "/trips" },
  { id: "mileage", title: "Mileage", icon: "mileage", path: "/mileage" },
  { id: "export", title: "Export", icon: "export", path: "/export" },
];

export function Shell({ section, lastUpload, topEnd, children }: {
  section: Section | null;
  lastUpload: Date | null | undefined;
  /** Shown on the right when there's no navigation, as on the subscription-ended screen. */
  topEnd?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="cv">
      <a className="cv-skip" href="#main">Skip to content</a>
      <header className="cv-top">
        <a className="cv-brand" href="/trips" onClick={linkHandler("/trips")}>
          <img src="/app-icon.png" alt="" width="34" height="34" />Carveny Online
        </a>
        {section && (
          <nav className="cv-nav" aria-label="Main">
            {sections.map((item) => (
              <a key={item.id} href={item.path} onClick={linkHandler(item.path)} aria-current={section === item.id ? "page" : undefined}>
                <Icon name={item.icon} />{item.title}
              </a>
            ))}
          </nav>
        )}
        {!section && topEnd && <div className="cv-top-end">{topEnd}</div>}
        {section && (
          <div className="cv-top-end">
            {lastUpload !== undefined && (
              <span className="cv-sync">
                <span className="cv-sync-dot" style={lastUpload ? undefined : { background: "var(--text-3)", boxShadow: "none" }} />
                {lastUpload ? `Last upload from your iPhone ${formatRelativeMoment(lastUpload)}` : "Nothing uploaded yet"}
              </span>
            )}
            <a
              className="cv-account"
              href="/account"
              onClick={linkHandler("/account")}
              aria-current={section === "account" ? "page" : undefined}
              style={section === "account" ? { borderColor: "var(--pine)", boxShadow: "0 0 0 3px var(--pine-tint-2)" } : undefined}
            >
              <span className="cv-avatar"><Icon name="person" /></span>Account
            </a>
          </div>
        )}
      </header>
      <main id="main" className="cv-main" tabIndex={-1}>{children}</main>
      <footer className="cv-footer">
        <a href="https://legal.buknal.com/carveny/privacy/">Privacy Policy</a>
        <a href="https://legal.buknal.com/carveny/terms/">Terms</a>
        <a href="https://support.buknal.com/carveny/">Help</a>
      </footer>
      {section && (
        <nav className="cv-tabbar" aria-label="Main">
          {[...sections, { id: "account" as const, title: "Account", icon: "person", path: "/account" }].map((item) => (
            <a key={item.id} href={item.path} onClick={linkHandler(item.path)} aria-current={section === item.id ? "page" : undefined}>
              <Icon name={item.icon} />{item.title}
            </a>
          ))}
        </nav>
      )}
    </div>
  );
}
